"use client";

import { Button } from "@ethera/ui/button";
import { NEXT_PUBLIC_SIGN_IN_URL } from "@guru/shared/config/env.client";
import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { TrialCTA } from "@/components/marketing/trial-cta";
import { PublicThemeToggle } from "./public-theme-toggle";
import { PWAInstallButton } from "./pwa-install-button";

export function PublicNav() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track active section on scroll (only on homepage)
  useEffect(() => {
    if (pathname !== "/home") {
      setActiveSection(null);
      return;
    }

    const sectionIds = ["features", "faq"];
    const observers: IntersectionObserver[] = [];

    const observerCallback = (id: string) => (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(id);
        }
      });
    };

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        const observer = new IntersectionObserver(observerCallback(id), {
          rootMargin: "-40% 0px -50% 0px",
          threshold: 0,
        });
        observer.observe(element);
        observers.push(observer);
      }
    });

    // Reset to null when at top of page
    const handleScrollForSection = () => {
      if (window.scrollY < 200) {
        setActiveSection(null);
      }
    };
    window.addEventListener("scroll", handleScrollForSection);

    return () => {
      for (const observer of observers) {
        observer.disconnect();
      }
      window.removeEventListener("scroll", handleScrollForSection);
    };
  }, [pathname]);

  const navLinks = [
    { href: "/home", label: t.nav.home, section: null },
    { href: "/home#features", label: t.nav.features, section: "features" },
    { href: "/roadmap", label: t.nav.roadmap, section: null },
    { href: "/pricing", label: t.nav.pricing, section: null },
    { href: "/blog", label: t.nav.blog, section: null },
    { href: "/contact", label: t.nav.contact, section: null },
  ];

  const isLinkActive = (link: { href: string; section: string | null }) => {
    // For section links on homepage
    if (link.section && pathname === "/home") {
      return activeSection === link.section;
    }
    // For Home link - active when on homepage and no section is active
    if (link.href === "/home" && !link.section) {
      return pathname === "/home" && !activeSection;
    }
    // For other page links
    return pathname === link.href;
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled || mobileMenuOpen ? "bg-background/95 backdrop-blur-md shadow-sm" : ""
      )}
    >
      <nav className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/home" className="flex items-center">
            <Image
              src="/logo.png"
              alt="EtheraGuru"
              width={163}
              height={32}
              className="h-10 w-auto dark:hidden"
            />
            <Image
              src="/logo-dark.png"
              alt="EtheraGuru"
              width={163}
              height={32}
              className="h-10 w-auto hidden dark:block"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "text-nav transition-colors relative nav-underline",
                  "hover:text-gray-900 dark:hover:text-gray-100",
                  isLinkActive(link)
                    ? "text-gray-900 dark:text-gray-100 active"
                    : "text-gray-600 dark:text-gray-400"
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1">
              <PWAInstallButton variant="ghost" size="sm" />
              <PublicThemeToggle />
            </div>
            <a href={NEXT_PUBLIC_SIGN_IN_URL} className="hidden md:block">
              <Button variant="ghost" size="sm">
                {t.nav.signIn}
              </Button>
            </a>
            <div className="hidden md:block">
              <TrialCTA placement="nav" label={t.nav.joinWaitlist} size="sm" />
            </div>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 border-t pt-4 bg-background">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "block px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  isLinkActive(link)
                    ? "text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700"
                    : "text-foreground/80 hover:bg-accent"
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-2 px-4 py-3 border-t border-border mt-2">
              <PublicThemeToggle />
            </div>
            <div className="space-y-2 px-4">
              <a
                href={NEXT_PUBLIC_SIGN_IN_URL}
                onClick={() => setMobileMenuOpen(false)}
                className="block"
              >
                <Button variant="outline" size="sm" className="w-full">
                  {t.nav.signIn}
                </Button>
              </a>
              <TrialCTA
                placement="nav-mobile"
                label={t.nav.joinWaitlist}
                size="sm"
                className="w-full"
              />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
